import * as THREE from "three";
import type { Vec2 } from "@dyson/shared";

/** Dashes and gaps along the line, in blocks. */
const DASH = 1.1;
const GAP = 0.9;
/**
 * The longest line there's room for, in dashes: a full brace throws you about
 * 870 blocks now (LEAP.maxSpeed over CHARACTER.airDrag), and the line has to
 * reach all of it to be worth aiming by.
 */
const MAX_DASHES = 460;
/** Over the characters and the rock. */
const Z = 1;
/** The braced line's colour: warm, against the blues of the sphere. */
const BRACE = 0xffd27a;

export interface PathView {
  from: Vec2;
  to: Vec2;
  /** It ends on rock: a ring shows where. */
  hit: boolean;
}

/**
 * A dotted line through open space: where a push off will take us while we
 * brace for it (see LEAP), with a ring on the rock it reaches. Shown only
 * then — drifting has no line, so nothing is drawn unless you're aiming.
 */
export class PathLine {
  readonly group = new THREE.Group();
  private readonly positions = new Float32Array(MAX_DASHES * 2 * 3);
  private readonly geometry = new THREE.BufferGeometry();
  private readonly material = new THREE.LineBasicMaterial({ transparent: true, depthTest: false, fog: false });
  private readonly ring = new THREE.Mesh(
    new THREE.RingGeometry(0.7, 1, 24),
    new THREE.MeshBasicMaterial({ transparent: true, depthTest: false, fog: false, side: THREE.DoubleSide }),
  );

  constructor() {
    this.geometry.setAttribute("position", new THREE.BufferAttribute(this.positions, 3));
    const line = new THREE.LineSegments(this.geometry, this.material);
    line.frustumCulled = false;
    line.renderOrder = 9;
    this.ring.renderOrder = 9;
    this.group.add(line, this.ring);
    this.group.visible = false;
  }

  update(path: PathView | null): void {
    this.group.visible = !!path;
    if (!path) return;
    const dx = path.to.x - path.from.x;
    const dy = path.to.y - path.from.y;
    const length = Math.hypot(dx, dy) || 1;
    const ux = dx / length;
    const uy = dy / length;
    let n = 0;
    for (let d = 1.5; d < length && n < MAX_DASHES; d += DASH + GAP, n++) {
      const end = Math.min(length, d + DASH);
      this.positions.set([path.from.x + ux * d, path.from.y + uy * d, Z, path.from.x + ux * end, path.from.y + uy * end, Z], n * 6);
    }
    this.geometry.setDrawRange(0, n * 2);
    this.geometry.attributes.position!.needsUpdate = true;
    const ring = this.ring.material as THREE.MeshBasicMaterial;
    this.material.color.setHex(BRACE);
    this.material.opacity = 0.9;
    ring.color.setHex(BRACE);
    ring.opacity = this.material.opacity;
    this.ring.visible = path.hit;
    this.ring.position.set(path.to.x, path.to.y, Z);
  }
}
